import { useBoolean } from "minimal-shared/hooks";
import { useRef, useEffect, useCallback } from "react";
import { isActiveLink, isExternalLink } from "minimal-shared/utils";

import { usePathname } from "src/routes/hooks";

import { NavItem } from "./nav-item";
import { navSectionClasses } from "../styles";
import { NavUl, NavLi, NavCollapse } from "../components";

import type { NavListProps, NavSubListProps } from "../types";
import { ConfirmDialog } from "src/components/custom-dialog";
import { Button } from "@mui/material";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/store/store";
import { useNavigate } from "react-router";
import { doLogout } from "src/store/auth/auth.action";
import { toast } from "sonner";

// ----------------------------------------------------------------------

export function NavList({
  data,
  depth,
  render,
  slotProps,
  currentRole,
  enabledRootRedirect,
}: NavListProps) {
  const pathname = usePathname();
  const confirm = useBoolean();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const resultAction = await dispatch(doLogout());

      if (doLogout.fulfilled.match(resultAction)) {
        sessionStorage.clear();
        localStorage.clear();
        const payload = resultAction.payload as { message: string };
        toast.success(payload.message);
        navigate("/login", { replace: true });
      } else {
        console.error("Logout failed:", resultAction.payload);
      }
    } catch (error) {
      console.error("Unexpected logout error:", error);
    }
  };

  const navItemRef = useRef<HTMLButtonElement | null>(null);

  const isActive = isActiveLink(pathname, data.path, !!data.children);

  const { value: open, onFalse: onClose, onToggle } = useBoolean(isActive);

  useEffect(() => {
    if (!isActive) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      onToggle();
    }
  }, [data.children, onToggle]);

  const renderLogoutDialog = () => (
    <ConfirmDialog
      open={confirm.value}
      onClose={confirm.onFalse}
      title="Logout"
      content="Are you sure you want to logout?"
      action={
        <Button variant="contained" color="error" onClick={handleLogout}>
          Logout
        </Button>
      }
    />
  );

  const renderNavItem = () => (
    <NavItem
      ref={navItemRef}
      // slots
      path={data.path}
      icon={data.icon}
      info={data.info}
      title={data.title}
      caption={data.caption}
      // state
      open={open}
      active={isActive}
      disabled={data.disabled}
      // options
      depth={depth}
      render={render}
      hasChild={!!data.children}
      externalLink={isExternalLink(data.path)}
      enabledRootRedirect={enabledRootRedirect}
      // styles
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      // actions
      onClick={() =>
        data.title === "Logout" ? confirm.onTrue() : handleToggleMenu()
      }
    />
  );

  const renderCollapse = () =>
    !!data.children && (
      <NavCollapse
        mountOnEnter
        unmountOnExit
        depth={depth}
        in={open}
        data-group={data.title}
      >
        <NavSubList
          data={data.children}
          render={render}
          depth={depth}
          slotProps={slotProps}
          currentRole={currentRole}
          enabledRootRedirect={enabledRootRedirect}
        />
      </NavCollapse>
    );

  // Hidden item by role
  if (data.roles && currentRole && !data.roles.includes(currentRole)) {
    return null;
  }

  return (
    <NavLi
      disabled={data.disabled}
      sx={{
        ...(!!data.children && {
          [`& .${navSectionClasses.li}`]: {
            "&:first-of-type": { mt: "var(--nav-item-gap)" },
          },
        }),
      }}
    >
      {renderNavItem()}
      {renderCollapse()}
      {renderLogoutDialog()}
    </NavLi>
  );
}

// ----------------------------------------------------------------------

function NavSubList({
  data,
  render,
  depth = 0,
  slotProps,
  currentRole,
  enabledRootRedirect,
}: NavSubListProps) {
  return (
    <NavUl sx={{ gap: "var(--nav-item-gap)" }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          slotProps={slotProps}
          currentRole={currentRole}
          enabledRootRedirect={enabledRootRedirect}
        />
      ))}
    </NavUl>
  );
}
